import {AuthScope, UserInfo} from "../types/userstate";
import {APP_LOCAL_STORAGE} from "./constants";
import {currentLocation, navigate} from "./router.tsx";
import {DataParams, error, KTableSortingDirection, Paths, success} from "@kopjra/uikit";

export function formatNumber(n: number): string {
    return (n > 9) ? `${n}` : `0${n}`;
}

export interface ReversedMap {
    [key: string]: string[];
}

export function reverseMapFromMap(map: { [key: string]: string }): ReversedMap {
    return Object.keys(map).reduce<ReversedMap>((previousValue, key) => {
        previousValue[map[key]] = (previousValue[map[key]] || []).concat([key]);
        return previousValue;
    }, {});
}

export function createURLSearchParams(obj: Record<string, string>): URLSearchParams {
    const usp = new URLSearchParams();
    for (const key of Object.keys(obj)) {
        if (obj[key] !== undefined && obj[key] !== null) {
            usp.append(key, obj[key]);
        }
    }
    return usp;
}

export function saveToLocalStorage(obj: object, where: string) {
    const json = JSON.stringify(obj);
    localStorage.setItem(`${APP_LOCAL_STORAGE}/${where}`, json);
}

export function loadFromLocalStorage(where: string): object {
    const json = localStorage.getItem(`${APP_LOCAL_STORAGE}/${where}`);
    return json ? JSON.parse(json) : {};
}

export function createLinkToDownload(name: string, url: string) {
    const link = document.createElement("a");
    link.style.display = "none";
    link.setAttribute("download", `${name}`);
    link.href = url;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

export function isControlOrCommand(keysym: number): boolean {
    switch (keysym) {
        case 65507:
        case 65508:
        case 65511:
        case 65512:
            return true;
        default:
            return false;
    }
}

export function mapCommandToControl(keysym: number): number {
    if (keysym === 65511) {
        return 65507;
    } else if (keysym === 65512) {
        return 65508;
    } else {
        return keysym;
    }
}

export function isUserAdmin(userInfo?: UserInfo): boolean {
    return !!(userInfo && (userInfo.scopes.indexOf(AuthScope.ADMIN) !== -1 || userInfo.scopes.indexOf(AuthScope.SUPERADMIN) !== -1));
}

export function isSuperAdmin(userInfo?: UserInfo): boolean {
    return !!(userInfo && userInfo.scopes.indexOf(AuthScope.SUPERADMIN) !== -1);
}

export function removeUndefinedOrNull<T extends object>(obj: T) {
    for (const key of Object.keys(obj)) {
        const typedKey = key as keyof T;
        if (obj[typedKey] === undefined || obj[typedKey] === null) {
            delete obj[typedKey];
        }
    }
    return obj;
}

// This type is ready for Api calls
export interface TransformedDataParams {
    top?: number;
    skip?: number;
    sort?: string;
    [key: string]: unknown;
}

export function paramsString(params: TransformedDataParams): string {
    return Object.keys(params).length > 0 ? "?" + new URLSearchParams(params as Record<string, string>).toString() : "";
}

export function transformDataParams<T>(params: DataParams<T>, extra?: Record<string, unknown>): TransformedDataParams {
    const convertedParams: TransformedDataParams = {};
    convertedParams.top = params.top;
    convertedParams.skip = params.skip;
    if (params.sort?.direction && params.sort.direction !== KTableSortingDirection.NONE) {
        convertedParams.sort = `${(params.sort.direction === KTableSortingDirection.DESC) ? "-" : ""}${String(params.sort.field)}`;
    }
    if (params.filters) {
        for (const key of Object.keys(params.filters) as Paths<T>[]) {
            const value = params.filters[key];
            convertedParams[key] = Array.isArray(value) ? value.join(",") : value;
        }
    }
    if (extra) {
        for (const key of Object.keys(extra)) {
            convertedParams[key] = extra[key];
        }
    }
    return convertedParams;
}

type SimpleRoute = string;
type ParamRoute = (id?: string) => string;
export type Route = SimpleRoute | ParamRoute;

type SimpleGoerType = (replace?: boolean) => void;
type ParamGoerType = (id: string, replace?: boolean) => void;

type RouteGoerType<RouteDefinition extends Record<string, Route>> = {
    [K in keyof RouteDefinition as `go${Capitalize<Lowercase<K & string>>}`]: RouteDefinition[K] extends SimpleRoute ? SimpleGoerType : ParamGoerType;
}

type RouteNameType<RouteDefinition extends Record<string, Route>> = {
    [K in keyof RouteDefinition as `route${Capitalize<Lowercase<K & string>>}`]: string;
}

type InRouteType<RouteDefinition extends Record<string, Route>> = {
    [K in keyof RouteDefinition as `in${Capitalize<Lowercase<K & string>>}`]: (location?: string) => boolean;
}

type SimpleExtensionGoerType = () => void;
type ParamExtensionGoerType = (id: string) => void;

type ExtensionsRouteGoerType<RouteDefinition extends Record<string, Route>> = Partial<{
    [K in keyof RouteDefinition as `ext${Capitalize<Lowercase<K & string>>}`]: RouteDefinition[K] extends SimpleRoute ? SimpleExtensionGoerType : ParamExtensionGoerType;
}>

export function createRoutes<T extends Record<string, Route>>(rConf: T, extensions?: ExtensionsRouteGoerType<T>): T & RouteGoerType<T> & RouteNameType<T> & InRouteType<T> {
    const routes: T & RouteGoerType<T> & RouteNameType<T> & InRouteType<T> = {} as T & RouteGoerType<T> & RouteNameType<T> & InRouteType<T>;
    for (const key of Object.keys(rConf)) {
        const route = rConf[key];
        const goKey = `go${key.charAt(0).toUpperCase()}${key.slice(1).toLowerCase()}` as keyof RouteGoerType<T>;
        const extKey = `ext${key.charAt(0).toUpperCase()}${key.slice(1).toLowerCase()}` as keyof ExtensionsRouteGoerType<T>;
        const nameKey = `route${key.charAt(0).toUpperCase()}${key.slice(1).toLowerCase()}` as keyof RouteNameType<T>;
        const inKey = `in${key.charAt(0).toUpperCase()}${key.slice(1).toLowerCase()}` as keyof InRouteType<T>;
        // @ts-expect-error - we certainly can add properties to the object
        routes[nameKey] = route instanceof Function ? route() : route;
        // @ts-expect-error - we certainly can add properties to the object
        routes[inKey] = (location?: string): boolean => {
            if (!location) {
                location = currentLocation.pathname;
            }
            const completeRoute = route instanceof Function ? route() : route;
            const cleanRoute = completeRoute.split("/:")[0];
            return location.startsWith(cleanRoute);
        }
        if (typeof route === "function") {
            // @ts-expect-error - we certainly can add properties to the object
            routes[goKey] = (id?: string, replace: boolean = false) => {
                if (extensions && extensions[extKey]) {
                    // @ts-expect-error - we are sure that it is a function
                    extensions[extKey](id);
                }
                navigate(route(id), {replace});
            };
        } else {
            // @ts-expect-error - we certainly can add properties to the object
            routes[goKey] = (replace: boolean = false) => {
                if (extensions && extensions[extKey]) {
                    // @ts-expect-error - we are sure that it is a function
                    extensions[extKey]();
                }
                navigate(route, {replace});
            };
        }
    }
    return {...rConf, ...routes};
}

export async function tryCall<T>(fn: Promise<T>, msgs?: {
    ok?: string,
    error?: string | (Record<number, string> & {default: string})
}, cb?: (ret?: T) => void | Promise<void>): Promise<T | null> {
    try {
        const ret: T = await fn;
        if (msgs?.ok) {
            success(msgs.ok);
        }
        if (cb) {
            await cb(ret);
        }
        return ret;
    } catch (err) {
        if (msgs?.error) {
            if (typeof msgs.error === "string") {
                error(msgs.error);
            } else {
                if (typeof err === "object" && err && "status" in err && msgs.error[+(err.status as string | number)]) {
                    error(msgs.error[+(err.status as string | number)]);
                } else {
                    error(msgs.error.default);
                }
            }
        }
        return null;
    }
}
